'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { resolveLegalModalContent } from '@/features/legal/lib/resolve-legal-modal-content';
import { Button } from '@/shared/ui/button';
import { ClientMDXRenderer } from '@/shared/ui/client-mdx-renderer';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './privacy-policy-modal.scss';

type Props = ModalProps & {
    onAccept?: () => void;
    children?: ReactNode;
};

type PrivacyPolicyModalContextValue = {
    onAccept?: () => void;
    onClose: ModalProps['onClose'];
    content: string;
};

const PrivacyPolicyModalContext = createContext<PrivacyPolicyModalContextValue | null>(null);

const usePrivacyPolicyModalContext = () => {
    const context = useContext(PrivacyPolicyModalContext);
    if (!context) {
        throw new Error('PrivacyPolicyModal components must be used within PrivacyPolicyModalWrapper.');
    }
    return context;
};

const usePrivacyPolicyContent = () => {
    const t = useTranslations();
    const { privacyPolicy } = useContext(AppConfigContext);
    return resolveLegalModalContent(privacyPolicy, t('LEGAL_CONTENT.PREPARING'));
};

export const PrivacyPolicyModalWrapper = ({ onAccept, children, ...props }: Props) => {
    const content = usePrivacyPolicyContent();

    return (
        <PrivacyPolicyModalContext.Provider
            value={{
                onAccept,
                onClose: props.onClose,
                content,
            }}
        >
            <Modal
                {...props}
                wrapperProps={{
                    className: 'privacy-policy-modal',
                    containerProps: {
                        className: 'privacy-policy-modal__container',
                    },
                    'data-testid': 'privacy-policy-modal',
                }}
            >
                {children}
            </Modal>
        </PrivacyPolicyModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const PrivacyPolicyModalProvider = ({ onAccept, children, ...props }: Props) => {
    const content = usePrivacyPolicyContent();

    return (
        <PrivacyPolicyModalContext.Provider
            value={{
                onAccept,
                onClose: props.onClose,
                content,
            }}
        >
            {children}
        </PrivacyPolicyModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const PrivacyPolicyModalTitle = () => {
    const t = useTranslations();
    return <>{t('PRIVACY_POLICY_MODAL.TITLE')}</>;
};

type ContentProps = ComponentProps<typeof Layout>;
const PrivacyPolicyModalContent = ({ media, ...props }: ContentProps) => {
    const { content } = usePrivacyPolicyModalContext();
    return (
        <Layout media={media} data-testid="privacy-policy-modal-content" {...props}>
            <ClientMDXRenderer content={content} />
        </Layout>
    );
};

type AcceptButtonProps = ComponentProps<typeof Button>;
const PrivacyPolicyModalAcceptButton = ({ onClick, ...props }: AcceptButtonProps) => {
    const t = useTranslations();
    const { onAccept, onClose } = usePrivacyPolicyModalContext();

    if (!onAccept) {
        return null;
    }

    return (
        <Layout
            element={Button}
            data-testid="privacy-policy-modal-accept-button"
            onClick={(event) => {
                onClick?.(event);
                onAccept?.();
                onClose();
            }}
            {...props}
        >
            {t('PRIVACY_POLICY_MODAL.ACCEPT_BUTTON')}
        </Layout>
    );
};

export { PrivacyPolicyModalWrapper as PrivacyPolicyModal };

export {
    PrivacyPolicyModalWrapper as Wrapper,
    PrivacyPolicyModalProvider as Provider,
    PrivacyPolicyModalTitle as Title,
    PrivacyPolicyModalContent as Content,
    PrivacyPolicyModalAcceptButton as AcceptButton,
};
